import { useState } from 'react';
import _ from 'lodash';
import { Tooltip } from '@material-ui/core';
import { resetTooltipForMenuButton } from './Tooltips/helpers';
import { Flex } from './base/layout-components';
import Icon from './base/Icon';
import Checkbox from './base/Checkbox';
import styled from 'styled-components';
import Text from './base/Text';
import { getColor } from 'common/constants/_colors';
import { LBButton, LBKebabIcon } from '@linearb/buzz';
import LimitedTextWithTooltip, {
  StyledLimitedTextWithTooltip
} from './base/LimitedTextWithTooltip';

const StyledIcon = styled(Icon)`
  :hover {
    color: ${getColor('chambray')} !important;
  }
  position: absolute;
  right: 1rem;
`;

const StyledText = styled(Text)`
  ${({ extraIcon }) => extraIcon && 'flex: 1;'}
`;

export const MenuItemWrapper = styled(Flex)`
  ${({ padding }) => `padding: ${padding ? padding : '1rem'};`}
  min-heigth: 2.2rem;

  & ${StyledLimitedTextWithTooltip} {
    & .text {
      color: ${getColor('shuttleGray')};
    }
  }

  ${({ isActive }) =>
    isActive &&
    `
      background: ${getColor('zircon')};
      border-top: 1px solid ${getColor('dodgerBlue')};
      border-bottom: 1px solid ${getColor('dodgerBlue')};
  `}

  ${({ disabled, iconHover }) =>
    disabled
      ? `cursor: default;
      svg {
        color: ${getColor('osloGray')} !important;
    }`
      : `cursor: pointer;
    :hover {
      background: ${getColor('zircon')} !important;
      p,
      a {
        color: ${getColor('dodgerBlue')};
      }
      svg > path[stroke],  svg > rect[stroke] {
        stroke: ${getColor('dodgerBlue')};
      }
     ${
       iconHover &&
       `,svg > path[fill] {
        fill: ${getColor('dodgerBlue')};
      }`
     }
    }`}
`;

export const ICON_POSITIONS = {
  beforeText: 'beforeText',
  afterText: 'afterText'
};

export const MenuButtonItem = ({
  onClick = _.noop,
  id,
  text,
  hoverText = null,
  icon = null,
  padding = null,
  disabled = false,
  isWithIcon = false,
  whiteSpace = null,
  tag = null,
  iconPos = ICON_POSITIONS.beforeText,
  isMultiple = false,
  checkboxOnChange = _.noop,
  selectedCheckboxes = [],
  isSelected = false,
  isActive = false,
  textStyle = {},
  iconHover = true,
  extraIcon = null,
  ...rest
}) => {
  const [isHover, setIsHover] = useState(false);
  return (
    <MenuItemWrapper
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      padding={isMultiple ? '0.5rem' : padding}
      onClick={disabled ? _.noop : onClick}
      items='center'
      justify='space-between'
      disabled={disabled}
      isActive={isActive}
      iconHover={iconHover}
      {...(extraIcon ? { width: '100%' } : {})}
      {...rest}
    >
      <Flex items='center' {...(extraIcon ? { width: '100%' } : {})}>
        {isMultiple && (
          <Checkbox
            disabled={disabled}
            checked={
              isSelected ||
              !!selectedCheckboxes.find(item =>
                item.id ? String(item.id) === String(id) : item.title === text
              )
            }
            onChange={e => {
              checkboxOnChange();
            }}
          />
        )}
        {iconPos === ICON_POSITIONS.beforeText ? icon : null}
        <StyledText
          size='medium'
          disabled={disabled}
          extraIcon={extraIcon}
          whiteSpace={whiteSpace}
          style={textStyle}
          {...(icon
            ? iconPos === ICON_POSITIONS.beforeText
              ? { ml: '0.8rem' }
              : { mr: '0.8rem' }
            : {})}
        >
          {text}
        </StyledText>

        {iconPos === ICON_POSITIONS.afterText ? icon : null}
        {extraIcon}
        {isWithIcon && disabled && (
          <Tooltip title='Select board first' placement='right'>
            <StyledIcon name='infooutlined' color='dodgerBlue' />
          </Tooltip>
        )}
      </Flex>
      {tag && tag}
      {isHover && hoverText && (
        <LimitedTextWithTooltip text={hoverText} max={12} placement='right' />
      )}
    </MenuItemWrapper>
  );
};

const MenuButton = ({
  items = [],
  placement = 'bottom-end',
  width = '15rem',
  padding = null,
  buttonText = null,
  dataCy = 'menu',
  hideBorder = false,
  size = 'small',
  clickable = true,
  closeOnMouseLeave = true,
  onHover = _.noop,
  buttonVariant = 'ghost',
  ...rest
}) => {
  const [open, setOpen] = useState(false);

  const openMenu = () => {
    setOpen(true);
    onHover();
  };
  const closeMenu = () => {
    setOpen(false);
  };

  return (
    <Tooltip
      interactive
      open={open}
      data-testid={dataCy}
      placement={placement}
      classes={resetTooltipForMenuButton()}
      title={
        <Flex
          width={width}
          direction='column'
          data-testid='menu-button-title-wrapper'
          {...(closeOnMouseLeave ? { onMouseLeave: closeMenu } : {})}
          onMouseEnter={openMenu}
          onClick={closeMenu}
        >
          {items}
        </Flex>
      }
    >
      <LBButton
        data-cy={dataCy}
        onMouseEnter={openMenu}
        onMouseLeave={closeMenu}
        variant={buttonVariant}
        size={buttonText ? 'default' : 'icon'}
        {...rest}
      >
        {buttonText ? buttonText : <LBKebabIcon />}
      </LBButton>
    </Tooltip>
  );
};

export default MenuButton;
